import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, createSearchParams, useLocation } from 'react-router-dom';
import classnames from 'classnames';
import _ from 'lodash';
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Label,
  FormGroup,
  Form,
  Input,
  Table,
  Row,
  Col,
  Spinner,
  UncontrolledTooltip,
  TabPane,
  CardFooter,
} from 'reactstrap';
import PageTabs from 'components/PageTabs';
import { FixedCenterLoader } from 'components/Loaders';
import TimePeriod, { formatDates } from 'components/TimePeriod';
import { AutoColumns } from 'components/Columns';
import { useArrayState } from 'components/CustomHooks';
import DownloadSimpleTable from 'components/DownloadSimpleTable';
import ReactDatetime from 'react-datetime';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { getCorrelationRolling } from 'services/analysis';
import { optionizeValue, optionizeAll, getValue, getDate, getYearMonth, monthDiff } from 'helpers/select';
import { confidenceLevelToInterval } from 'helpers/formatter';
import { toDeci, toPerc, toPercTable, toDeciTable, tablePresets, getDataType, formatCurrency } from 'helpers/formatter';
import PieChart from 'components/PieChart';
import TightTable from 'components/TightTable';
import { Descriptions, Notes } from '../others';
import MultipleAssetSelect from '../MultipleAssetSelect';
import RollingCorrelationBase from './rolling-correlation/RollingCorrelation';
import AssetHeader from 'views/AssetHeader';
import ToggleContainer from 'components/ToggleContainer';

function RollingCorrelation({ session }) {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({ product: [], database: [], window: '12' });
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);

    const [data, err] = await getCorrelationRolling(options);

    setResult(data);
    setError(err);
    setLoading(false);
  };

  useEffect(() => {
    if (options.product.length === 0) return;
    fetchData();
  }, [options]);

  let main = null;
  if (loading) {
    main = <FixedCenterLoader />;
  } else if (result) {
    const infoHeaderData = {
      'Asset 1': result.header['Asset 1'],
      'Asset 1 Type': result.header['Asset 1 Type'],
      'Asset 2': result.header['Asset 2'],
      'Asset 2 Type': result.header['Asset 2 Type'],
      'Rolling Period': result.header['window'],
    };

    main = (
      <>
        <div className="tw-text-3xl tw-font-bold tw-mb-1 tw-text-center">Rolling Correlation</div>
        <AssetHeader asset={result.header['Fund Name']} />

        <Card>
          <CardHeader tag="h4" className="mt-0 text-center">
            {_.map(infoHeaderData, (val, key) => {
              if (!val) return null;
              return (
                <div className="tw-text-base">
                  {key}: <span className="tw-font-semibold">{val}</span>
                </div>
              );
            })}
          </CardHeader>
          <CardBody></CardBody>
        </Card>

        <Descriptions content={result.description} />
        <RollingCorrelationBase data={{ chart: result.chart, table: result.table }} params={options} />
        <Notes content={result.notes} />
      </>
    );
  } else if (error) {
    main = (
      <>
        <div className="tw-p-4 tw-mb-4 tw-text-sm tw-text-blue-800 tw-rounded-lg tw-bg-blue-100" role="alert">
          <span className="tw-font-bold">{error}</span>
        </div>
      </>
    );
  }

  const sideMenu = (
    <MultipleAssetSelect
      session={session}
      onSelect={({ assets, databases, period }) => {
        setOptions({ product: assets, database: databases, window: period });
      }}
      title="SELECT (2) ASSETS"
      minItems={2}
      maxItems={2}
      enablePeriod
    />
  );

  const mainContent = <div key={options.product.join('_')}>{main}</div>;

  return (
    <ToggleContainer
      sideMenu={sideMenu}
      mainContent={options.product.length > 0 && mainContent}
      classNames={{ sideMenu: 'tw-w-[400px]' }}
    />
  );
}

export default RollingCorrelation;
